import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Input, Select } from './FormComponents';
import { getUsersByRole, type AudienceUser } from '../../../../../services/PagesServices/SuperAdminMarketingService';

export interface ApprovalSectionProps {
  requiresApproval: boolean;
  approverUserId: string;
  approverEmail: string;
  onRequiresApprovalChange: (value: boolean) => void;
  onApproverUserIdChange: (value: string) => void;
  onApproverEmailChange: (value: string) => void;
}

const ApprovalSection: React.FC<ApprovalSectionProps> = ({
  requiresApproval,
  approverUserId,
  approverEmail,
  onRequiresApprovalChange,
  onApproverUserIdChange,
  onApproverEmailChange,
}) => {
  const [users, setUsers] = useState<AudienceUser[]>([]);
  const [mode, setMode] = useState<'user' | 'email'>(approverEmail ? 'email' : 'user');

  // Fetch the non-learner approver candidates once approval is enabled.
  useEffect(() => {
    if (!requiresApproval || users.length) return;
    (async () => {
      const res = await getUsersByRole();
      if (res.success && res.data) setUsers(res.data.filter((u) => u.roleId !== 1));
    })();
  }, [requiresApproval]);

  const switchMode = (next: 'user' | 'email') => {
    setMode(next);
    if (next === 'user') onApproverEmailChange('');
    else onApproverUserIdChange('');
  };

  return (
    <div className="settings-section">
      <div className="section-title">
        <ShieldCheck size={18} />
        <h4>Approval</h4>
      </div>
      <p className="section-description">Require a designated approver to sign off before this course goes live.</p>

      <div className="toggle-row">
        <div>
          <strong>Requires Approval</strong>
          <p>The course must be approved before it is published</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(e) => onRequiresApprovalChange(e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      {requiresApproval && (
        <>
          <div className="visibility-options" style={{ marginTop: 12 }}>
            <button
              type="button"
              className={`visibility-option ${mode === 'user' ? 'active' : ''}`}
              onClick={() => switchMode('user')}
            >
              <div className="visibility-content">
                <strong>Existing user (request approval)</strong>
                <p>An approval request lands on their screen; publishing is blocked until they approve.</p>
              </div>
            </button>
            <button
              type="button"
              className={`visibility-option ${mode === 'email' ? 'active' : ''}`}
              onClick={() => switchMode('email')}
            >
              <div className="visibility-content">
                <strong>Email address (notify)</strong>
                <p>An email is sent that a course has been added with their approval.</p>
              </div>
            </button>
          </div>

          {mode === 'user' ? (
            <div className="form-group" style={{ marginTop: 12 }}>
              <Select
                label="Approver"
                value={approverUserId}
                onChange={(e) => onApproverUserIdChange(e.target.value)}
              >
                <option value="">Select an approver</option>
                {users.map((u) => (
                  <option key={u.uuid} value={u.uuid}>
                    {u.name?.trim() || u.email} ({u.email})
                  </option>
                ))}
              </Select>
              <small className="form-help">Learners cannot be approvers.</small>
            </div>
          ) : (
            <div className="form-group" style={{ marginTop: 12 }}>
              <Input
                label="Approver email"
                type="email"
                value={approverEmail}
                onChange={(e) => onApproverEmailChange(e.target.value)}
                placeholder="approver@example.com"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApprovalSection;
